import { getPageBySlugs, type WpPage, type WpPost, type WpRendered } from "@/lib/wp";

export type WpSeo = {
  id?: number;
  slug?: string;
  head?: string | null;
  title?: string | null;
  description?: string | null;
  canonical?: string | null;
  robots?: string | null;
};

const TITLE_REGEX = /<title[^>]*>([\s\S]*?)<\/title>/i;
const DESCRIPTION_REGEX = /<meta[^>]*name=["']description["'][^>]*content=["']([^"']*)["'][^>]*>/i;
const CANONICAL_REGEX = /<link[^>]*rel=["']canonical["'][^>]*href=["']([^"']*)["'][^>]*>/i;
const ROBOTS_REGEX = /<meta[^>]*name=["']robots["'][^>]*content=["']([^"']*)["'][^>]*>/i;

function decodeHtmlEntities(raw: string): string {
  return raw
    .replace(/&#(x?[0-9a-fA-F]+);/g, (_, entity) => {
      const isHex = entity.startsWith("x") || entity.startsWith("X");
      const codePoint = isHex ? parseInt(entity.slice(1), 16) : Number(entity);
      if (!Number.isFinite(codePoint) || codePoint <= 0) return "";
      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return "";
      }
    })
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ");
}

function cleanString(value?: string | null): string | null {
  if (!value) return null;
  const decoded = decodeHtmlEntities(value);
  const normalized = decoded.replace(/\s+/g, " ").trim();
  return normalized.length ? normalized : null;
}

function stripRendered(value?: WpRendered | null): string | null {
  if (!value?.rendered) return null;
  const withoutTags = value.rendered.replace(/<[^>]+>/g, " ");
  return cleanString(withoutTags);
}

function extractFromHead(head?: string | null) {
  if (!head) return {} as const;
  const titleMatch = TITLE_REGEX.exec(head);
  const descriptionMatch = DESCRIPTION_REGEX.exec(head);
  const canonicalMatch = CANONICAL_REGEX.exec(head);
  const robotsMatch = ROBOTS_REGEX.exec(head);

  return {
    title: cleanString(titleMatch?.[1] ?? null),
    description: cleanString(descriptionMatch?.[1] ?? null),
    canonical: cleanString(canonicalMatch?.[1] ?? null),
    robots: cleanString(robotsMatch?.[1] ?? null),
  } as const;
}

export function extractSeoMeta(source: (WpPost | WpPage) | null): WpSeo | null {
  if (!source) return null;

  const head = typeof source.aioseo_head === "string" ? source.aioseo_head.trim() : "";
  const summary = extractFromHead(head || null);

  const fallbackTitle = stripRendered(source.title) ?? cleanString(source.slug);
  const fallbackDescription =
    "excerpt" in source && source.excerpt ? stripRendered(source.excerpt) : null;
  const canonical = summary.canonical ?? (typeof source.link === "string" ? cleanString(source.link) : null);

  const seo: WpSeo = {
    id: source.id,
    slug: source.slug,
    head: head || null,
    title: summary.title ?? fallbackTitle ?? null,
    description: summary.description ?? fallbackDescription ?? null,
    canonical: canonical ?? null,
    robots: summary.robots ?? null,
  };

  return seo;
}

export async function getPageSeo(slugs: string[], lang?: string): Promise<WpSeo | null> {
  const ordered = Array.isArray(slugs)
    ? slugs.map((slug) => slug.trim()).filter(Boolean)
    : [];
  if (ordered.length === 0) return null;
  const page = await getPageBySlugs(ordered, lang);
  if (!page) return null;
  return extractSeoMeta(page);
}

export function mergeSeo(primary?: WpSeo | null, fallback?: WpSeo | null): WpSeo | null {
  if (!primary && !fallback) return null;
  if (!primary) return fallback ?? null;
  if (!fallback) return primary;
  return {
    id: primary.id ?? fallback.id,
    slug: primary.slug ?? fallback.slug,
    head: primary.head ?? fallback.head,
    title: primary.title ?? fallback.title,
    description: primary.description ?? fallback.description,
    canonical: primary.canonical ?? fallback.canonical,
    robots: primary.robots ?? fallback.robots,
  };
}
